// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## musicalClock.js
//
// Keeps track of musical time (measures, beats, subdivisions) in relation to
// clock time (context.currentTime).
//
// Schedules playback of events in either language, buffering according to
// musical time, and adjusting as needed.
//
// Uses events to schedule playback of the next measure 25 MS ahead of the
// beginning of the measure.
import type { TimeSignatureAlias } from 'shared/definitions/musicTheory/rhythms';
import utils from '../utils';
import PianoInstrument from './pianoInstrument';
import Pointer from './pointer';
import Rhythm from './rhythm';
import TimeSignature from './timeSignature';
import type SoundBufferHolder from './soundBufferHolder';
import type { SourceHolder } from './soundBufferHolder';
import type NoteHolder from './noteHolder';
import type Music from './music';
import type Voice from './voice';
import type Note from './note';
import type TablaInstrument from './tablaInstrument';
import type Recorder from './recorder';

interface OnStartOrEnd {
  callback: Function;
  thisArg?: any;
  /** Argument to pass to the callback function */
  arg?: any;
}

type Sound =
  | string
  | number
  | Note
  | NoteHolder
  | Voice
  | Music
  | (string | number | Note | NoteHolder | Voice | Music)[];

interface ProcessedQueueObject {
  startTime: number;
  startPointer: Pointer;
  stopTime?: number;
  stopPointer?: Pointer;
  durationTime?: number;
  durationBeats?: number;
}

interface QueueObject extends ProcessedQueueObject {
  sound: Sound;
  instrument?: PianoInstrument | TablaInstrument;
  velocity?: number;
  onEnded?: OnStartOrEnd;
  onStarted?: OnStartOrEnd;
  sources?: SourceHolder[];
  /** Unique index added once pushed */
  qIndex?: number;
}

interface TimingsObject {
  /** Beat to start at */
  startBeat?: number;
  /** Pointer to start at */
  startPointer?: Pointer | string;
  /** Time in seconds to start at */
  startTime?: number; // seconds
  /** Number of downbeats from now to start at */
  nextMeasure?: number; // int
  /** Number of beats from now to start at */
  nextBeat?: number; // int
  /** Rhythm from now to start at */
  nextRhythm?: Rhythm;
  /** Beat to stop at */
  stopBeat?: number;
  /** Pointer to stop at */
  stopPointer?: Pointer;
  /** Time in seconds to stop at */
  stopTime?: number;
  /** Duration in seconds from start to stop at */
  durationTime?: number;
  /** Duration in beats from start to stop at */
  durationBeats?: number;
  /** Duration as a Pointer from start to stop at */
  durationPointer?: Pointer;
  onEnded?: OnStartOrEnd;
  onStarted?: OnStartOrEnd;
}

interface PreprocessedQueueObject extends TimingsObject {
  /** sound: soundName or [soundName, soundName, soundName] */
  sound: Sound;
  /** float, max 1.0 */
  velocity?: number;
  /** Instrument string name, as loaded in pianoInstrument */
  instrument?: PianoInstrument | TablaInstrument;
}

interface Options {
  meter?: TimeSignatureAlias | TimeSignature;
  tempo?: number;
  /** Whether to play a click track */
  click?: boolean;
}

class MusicalClock {
  /** Whether to play a click track */
  click = true;

  player: PianoInstrument;

  buffer: SoundBufferHolder;

  context: AudioContext;

  meter: TimeSignature;

  pointer: Pointer;

  /** In beats per minute, aka MM */
  tempo: number;

  /** Beats per second */
  beatSec: number;

  playbackQueue: QueueObject[];

  /** Unique index of queued playback notes to delete */
  qIndex: number;

  isRunning: boolean;

  startTime: number;

  /** Future pointer at which to stop playback or recording */
  stopPointer: Pointer;

  /** Holds one measure of metronome clicks */
  metronomeQ: PreprocessedQueueObject[];

  /** Called each time a beat occurs */
  private metronomeCallbacks: ((beat: number) => any)[] = [];

  /**
   *
   * @param {object} options
   * @param {string | TimeSignature} options.meter
   * @param {number} options.tempo
   * @param {boolean} options.click whether to provide a click track
   */
  constructor(options: Options = {}) {
    options = {
      meter: '4/4',
      tempo: 60,
      click: true,
      ...options,
    };

    this.click = !!options.click;
    this.setMeter(options.meter);
    this.setTempo(options.tempo);
    // this.updateMetronome();

    this.player = new PianoInstrument();
    this.player.loadTablaSounds();
    this.buffer = this.player.buffer;
    this.context = this.player.buffer.context;

    this.playbackQueue = [];
    this.qIndex = 0; // unique index of queued playback notes to delete.
    this.isRunning = false;
    this.startTime = undefined;
  }

  /**
   * Takes an object of starting & ending times, as well as sound
   * objects and/or callbacks.
   * {
   * TIMING PROPERTIES:
   *  startBeat : int          stopBeat
   *  startPointer : pointer    stopPointer
   *  startTime : seconds       stopTime
   *  nextMeasure : int         durationTime : seconds
   *    (for # of measures)     durationBeats : int
   *  nextBeat : boolean
   *  nextRhythm : rhythm
   *
   * SOUND PROPERTIES:
   *  sound : soundName or [soundName, soundName, soundName]
   *      or any music object that has getMidiMessages()
   *  velocity : float, Max 1.0
   *  instrument : string instrument name, as loaded in pianoInstrument
   *
   * CALLBACKS:
   *  onEnded and onStarted : {
   *      callback : function,
   *      thisArg  : this,
   *      arg      : arg to pass
   *     }
   * }
   * @param {Object} o
   * @returns {MusicalClock} this
   */
  queue(o: PreprocessedQueueObject) {
    // o for options; q for queue object; p = this.pointer.
    if (typeof o.sound === 'undefined') {
      throw new Error('soundname: A soundname must be defined to queue playback.');
    }

    const q: QueueObject = {
      sound: o.sound,
      instrument: o.instrument,
      velocity: o.velocity,
      ...this.processTime(o),
    };

    if (typeof o.onEnded !== 'undefined') {
      // onEnded must be an object in format:
      //    { callback : function, thisArg : this, args : arguments}
      q.onEnded = o.onEnded;
    }
    if (typeof o.onStarted !== 'undefined') {
      q.onStarted = o.onStarted;
    }

    // If we're stopped, just add it to the queue.
    // But if we're playing, add it and play it!
    this.push(q);
    return this;
  }

  onTime(timeObject: TimingsObject, callback: Function, thisArg?: any, arg?: any) {
    // Figure out the starttime if we need it.
    if (!timeObject.startTime && timeObject.startPointer) {
      timeObject = {
        ...this.processTime(timeObject),
        ...timeObject,
      };
    }

    const preprocessedQueueObj: PreprocessedQueueObject = {
      onEnded: {
        callback,
        thisArg,
        arg,
      },
      sound: 'silence',
      ...timeObject,
    };

    if (typeof preprocessedQueueObj.startTime === 'undefined') {
      this.queue(preprocessedQueueObj);
    } else {
      this.push(preprocessedQueueObj as QueueObject);
    }
    return this;
  }

  processTime(o: TimingsObject) {
    const defined = (prop) => typeof o[prop] !== 'undefined';

    const startData = this.setQTime(
      o.startBeat,
      o.startPointer,
      o.startTime,
      o.nextMeasure,
      o.nextBeat,
      o.nextRhythm
    );

    const q: ProcessedQueueObject = {
      startTime: startData.time,
      startPointer: startData.pointer,
    };

    if (defined('durationTime')) {
      q.stopTime = startData.time + o.durationTime;
    }

    if (defined('durationBeats')) {
      q.stopTime = startData.time + o.durationBeats * this.beatSec;
    }

    if (defined('durationPointer')) {
      q.stopTime = startData.time + this.getSeconds(o.durationPointer);
    }

    if (defined('stopTime') || defined('stopPointer') || defined('stopBeat')) {
      const stopData = this.setQTime(o.stopBeat, o.stopPointer, o.stopTime);
      q.stopTime = stopData.time;
      q.stopPointer = stopData.pointer;
      q.durationTime = q.stopTime - q.startTime;
      q.durationBeats = q.stopPointer.clone().subtract(q.startPointer).toBeats();
    }
    return q;
  }

  setQTime(
    beat: number,
    pointer: Pointer | string,
    seconds: number,
    nextMeasure?: number,
    nextBeat?: number,
    nextRhythm?: Rhythm
  ) {
    const q: { pointer: Pointer; time: number } = {
      pointer: this.getPointer(),
      time: this.getSeconds(),
    };
    // Setup startPointer & startTime.
    if (typeof beat !== 'undefined') {
      const p = new Pointer(`0:${beat}`, this.meter);
      p.simplify();
      q.pointer = p;
      q.time = beat * this.beatSec;
      return q;
    }

    if (typeof pointer !== 'undefined') {
      if (!(pointer instanceof Pointer)) {
        pointer = new Pointer(pointer, this.meter);
      }
      if (typeof nextMeasure !== 'undefined') {
        const p = this.getPointer();
        pointer = pointer.clone();
        pointer.m += p.m + nextMeasure;
      }
      q.pointer = pointer;
      q.time = this.getSeconds(pointer);

      return q;
    }

    if (typeof seconds !== 'undefined') {
      if (!utils.isNumeric(seconds)) {
        throw new Error('startTime or stopTime must be a number in seconds.');
      }
      if (!utils.isNumber(seconds)) {
        seconds = Number(seconds);
      }
      q.time = seconds;
      q.pointer = this.getPointer(seconds);
      return q;
    }

    if (typeof nextBeat !== 'undefined') {
      const p = this.getPointer();
      const b = p.b + 1;
      q.pointer = new Pointer(`${p.m}:${b}`, this.meter);
      q.time = this.getSeconds(q.pointer);
      return q;
    }

    if (typeof nextMeasure !== 'undefined') {
      const p = this.getPointer();
      const m = p.m + 1;
      q.pointer = new Pointer(`${m}:0`, this.meter);
      q.time = this.getSeconds(q.pointer);
      return q;
    }

    if (typeof nextRhythm !== 'undefined') {
      const p = this.getPointer();
      const r = new Rhythm(nextRhythm);
      q.pointer = new Pointer(`${p.m}:${p.b}`, this.meter);
      q.pointer.add(r);
      q.time = this.getSeconds(q.pointer);
      return q;
    }

    return q; // no time specified, make an assumption!
  }

  push(q: QueueObject) {
    this.qIndex++;
    q.qIndex = this.qIndex;
    this.playbackQueue.push(q);
    if (this.isRunning) this.startSound(q);
  }

  /**
   * Sets the starting pointer -- useful to allow countoffs to occupy negative pointer space.
   */
  setStartingPointer(pointer: string | Pointer) {
    pointer = Pointer.anythingToPointer(pointer, this.meter);
    this.startingPointer = pointer;
  }

  private startingPointer: Pointer;

  get startTimeOffset(): number {
    if (!this.startingPointer) return 0;
    return -this.startingPointer.toSeconds(this.tempo);
  }

  /**
   * Start playback of the queue.
   * Accepts object with { stopTime, stopPointer, stopBeat }
   * to schedule an end to playback. Playback start time CANNOT be configured.
   *
   * @param {Object} [o]
   * @returns this
   */
  play(o?: { stopTime?: number; stopPointer?: Pointer; stopBeat?: number }) {
    // delete this.stopPointer;
    if (o) {
      this.stop(o); // if o has stop info on it, we should stop then.
    }

    this.clickBeat();
    this.isRunning = true;
    this.startTime = this.buffer.context.currentTime + 0.02;
    this.playbackQueue.forEach((queuedItem) => this.startSound(queuedItem));
    return this;
  }

  // Same properties as queue:
  //  startBeat : init          stopBeat
  //  startPointer : pointer    stopPointer
  //  startTime : seconds       stopTime
  //  nextMeasure : int         durationTime : seconds
  //    (for # of measures)     durationBeats : int
  //  nextBeat : boolean
  //  nextRhythm : rhythm
  recordAt(o: TimingsObject, recorder: Recorder) {
    const q = this.processTime(o);
    if (typeof q.stopTime === 'undefined') {
      throw new Error(
        'o.stopTime, o.stopBeat, or o.stopPointer must be provided to schedule recording.'
      );
    }

    // start a half second early in case someone anticipates a note.
    // stop on time -- should only effect release.
    const startTime = Math.max(q.startTime - 0.5, 0);

    this.onTime(
      { startTime },
      recorder.record,
      recorder, // thisArg
      [q.startPointer.clone(), q.stopPointer.clone()] // args
    );
    this.onTime(
      { startTime: q.stopTime },
      recorder.stop,
      recorder, // thisArg
      q.stopPointer.clone()
    );
  }

  /**
   * stop playing now or at a given pointer:
   * { stopBeat, stopPointer, or stopTime }
   * @param o
   * @returns this
   */
  stop(o?: { stopBeat?: number; stopPointer?: Pointer; stopTime?: number }) {
    if (o) {
      const { stopBeat, stopPointer, stopTime } = o;
      // If we have a stopBeat, stopPointer or stopTime, stop at that time.
      if ([stopBeat, stopPointer, stopTime].filter((el) => typeof el !== 'undefined').length) {
        const stopObj = this.setQTime(o.stopBeat, o.stopPointer, o.stopTime);
        this.stopPointer = stopObj.pointer;
        // Slight kluge of adding 0.001 to be sure any attached events fire.
        return this.onTime({ startTime: stopObj.time + 0.001 }, () => {
          this.stop();
        });
      }
    }

    this.isRunning = false;
    this.playbackQueue.forEach((q) => {
      if (!q.sources) return;
      q.sources.forEach((source) => {
        this.buffer.panicSource(source);
      });
    });
    this.playbackQueue = [];
    this.qIndex = 0;
    delete this.stopPointer;
    return this;
  }

  trash(qIndex: number) {
    const index = this.playbackQueue.findIndex((q) => q.qIndex === qIndex);
    if (index !== -1) {
      this.playbackQueue.splice(index, 1);
    }
  }

  startSound(q: QueueObject) {
    const startTime = q.startTime + this.startTime + this.startTimeOffset;
    let stopTime;
    if (typeof q.stopTime !== 'undefined') {
      stopTime = q.stopTime + this.startTime + this.startTimeOffset;
    }
    if (Number.isNaN(Number(startTime))) {
      console.log('Not a valid startTime:');
      console.log(q);
      return;
    }

    if (startTime < 0) return;

    // Cast q.sound as an array if needed, and queue them up.
    if (!Array.isArray(q.sound)) {
      q.sound = [q.sound];
    }

    q.sources = [];
    // If it's an object w/ getMidiMessages() call playMusic
    // otherwise, we play them manually:
    q.sound.forEach((sound) => {
      if (typeof sound === 'object' && sound.getMidiMessages) {
        this.buffer.playMusic(sound, { tempo: this.tempo, startTime });
      } else if (typeof sound !== 'object') {
        this.buffer.play(sound, {
          startTime,
          stopTime,
          velocity: q.velocity,
          absoluteTiming: true,
          instrument: q.instrument,
        });
      }
      q.sources.push(this.buffer.sourceHolders.slice(-1)[0]);
    });
    this.buffer.onEnded(this.trash, this, q.qIndex);
    if (typeof q.onEnded !== 'undefined') {
      this.buffer.onEnded(q.onEnded.callback, q.onEnded.thisArg, q.onEnded.arg);
    }
    if (typeof q.onStarted !== 'undefined') {
      this.buffer.onStarted(q.onStarted.callback, q.onStarted.thisArg, q.onStarted.arg);
    }
  }

  setMeter(ts: TimeSignature | TimeSignatureAlias) {
    if (typeof ts === 'undefined') ts = '4/4';
    if (!(ts instanceof TimeSignature)) {
      ts = new TimeSignature(ts);
    }
    this.pointer = new Pointer('0:0', ts);
    this.meter = ts;
    this.playbackQueue = [];
    return this;
  }

  setTempo(bpm: number) {
    this.tempo = bpm;
    this.beatSec = 60 / bpm;
    this.playbackQueue = [];
    return this;
  }

  enableMetronome() {
    if (this.isRunning && !this.click) this.clickBeat();
    this.click = true;
    return this;
  }

  disableMetronome() {
    this.click = false;
    return this;
  }

  private clickBeat() {
    if (!this.click) return;
    const pointer = this.getPointer();
    const upcomingBeat = pointer.s === 0 ? pointer.clone() : pointer.getNextBeat();
    // Don't click if we don't need to.
    if (this.stopPointer && upcomingBeat.isGreaterThanOrEquals(this.stopPointer)) return;

    const sound = upcomingBeat.b === 0 ? 'tick' : 'tock';
    this.queue({
      sound,
      velocity: 0.6,
      startPointer: upcomingBeat,
    });
    // Trigger any metronomeCallbacks that exist:
    this.onTime(
      {
        startPointer: upcomingBeat,
      },
      () => {
        this.metronomeCallbacks.forEach((cb) => cb(upcomingBeat.b));
      }
    );
    // And set us up to click the next beat:
    const twoFiftyMillisecondsSubdivision = this.beatSec * 0.25;
    this.onTime(
      {
        startPointer: upcomingBeat.getNextBeat().subtract(`0:${twoFiftyMillisecondsSubdivision}:0`),
      },
      () => {
        this.clickBeat();
      }
    );
  }

  stopMetronome(o?: TimingsObject) {
    if (typeof o === 'undefined') {
      this.disableMetronome();
      return this;
    }

    utils.merge(o, this.processTime(o));
    // Stop it before it has a chance to hit the next beat.
    const startPointer = Pointer.anythingToPointer(o.startPointer, this.meter);
    startPointer.subtract('0:1:0');
    o.startTime = this.getSeconds(startPointer);
    this.onTime(
      { startTime: o.startTime },
      () => {
        this.disableMetronome();
      },
      this
    );
    return this;
  }

  addMetronomeCallback(callback: (beat: number) => any): void {
    this.metronomeCallbacks.push(callback);
  }

  startMetronome(o?: TimingsObject) {
    if (typeof o === 'undefined') {
      this.enableMetronome();
      return this;
    }
    utils.merge(o, this.processTime(o));
    const startPointer = Pointer.anythingToPointer(o.startPointer, this.meter);
    // Queue it up just ahead of the beat.
    startPointer.subtract('0:0.5:0');
    o.startTime = this.getSeconds(startPointer);

    this.onTime(
      { startTime: o.startTime },
      () => {
        this.enableMetronome();
      },
      this
    );
    return this;
  }

  /**
   * Gets a pointer for seconds. Seconds always measured as seconds from the starting pointer,
   * which might not be 0:0:0. This means that 0 seconds may not be 0:0:0
   */
  getPointer(seconds: number = this.getSeconds()) {
    const b = seconds / this.beatSec;
    return this.pointer.clone().setFromBeats(b);
  }

  /**
   * Gets seconds for a pointer. Seconds always measured as seconds from the starting pointer,
   * which might not be 0:0:0. This means that 0:0:0 may not be at 0 seconds.
   * */
  getSeconds(pointerOrPointerString?: Pointer | string) {
    if (typeof pointerOrPointerString === 'undefined') {
      if (this.isRunning) return this.context.currentTime - (this.startTime + this.startTimeOffset);
      return 0 - this.startTimeOffset;
    }
    pointerOrPointerString = this.pointer.anythingToPointer(pointerOrPointerString);
    const seconds = pointerOrPointerString.toBeats() * this.beatSec;
    return seconds;
  }
}

export default MusicalClock;
